import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PREVIEW_COACH_NAME } from "@/data/leaderPreviewData";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

interface InvitationContext {
  coachId: string;
  coachName: string;
  coachRole: string | null;
  coachAvatarUrl: string | null;
  message: string | null;
  dismissed: boolean;
}

interface ProfileData {
  coach_id?: string;
  dismissed_invitation_banner?: boolean;
}

interface InviteData {
  coach_name?: string;
  coach_role?: string;
  coach_invitation_message?: string;
}

export function useInvitationContext() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["invitation-context", user?.id],
    queryFn: async (): Promise<InvitationContext | null> => {
      if (!user) return null;
      if (user.id === PREVIEW_LEADER_USER_ID) {
        return {
          coachId: "preview-coach-user",
          coachName: PREVIEW_COACH_NAME,
          coachRole: "Executive Coach",
          coachAvatarUrl: null,
          message: "I connected Coach Veera so we can bring more real moments into our coaching conversations.",
          dismissed: false,
        };
      }

      // coach_leader_links is the single source of truth for coach identity
      const { data: coachLink } = await supabase
        .from("coach_leader_links")
        .select("coach_id")
        .eq("leader_id", user.id)
        .maybeSingle();

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("coach_id, dismissed_invitation_banner")
        .eq("id", user.id)
        .maybeSingle() as { data: ProfileData | null; error: unknown };

      if (profileError) {
        return null;
      }

      const activeCoachId = coachLink?.coach_id;

      if (!activeCoachId) {
        return null;
      }

      const { data: invite } = await supabase
        .from("coach_invites")
        .select("coach_name, coach_role, coach_invitation_message")
        .eq("coach_id", activeCoachId)
        .maybeSingle() as { data: InviteData | null; error: unknown };

      let coachName = invite?.coach_name?.trim() || "";
      let coachAvatarUrl: string | null = null;

      // Always fetch coach profile for avatar and fallback name
      const { data: coachProfile } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", activeCoachId)
        .maybeSingle();

      if (!coachName) {
        coachName = coachProfile?.full_name?.trim() || "Your Coach";
      }
      
      coachAvatarUrl = coachProfile?.avatar_url || null;

      return {
        coachId: activeCoachId,
        coachName: coachName || "Your Coach",
        coachRole: invite?.coach_role || null,
        coachAvatarUrl,
        message: invite?.coach_invitation_message || null,
        dismissed: profile.dismissed_invitation_banner ?? false,
      };
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });
}
